import { assign, round } from "@src/v3.0.0/lib/utils";
import { CROPPER_FRAME_OFFSET_TOP_PX, CROPPER_FRAME_PADDING_PCT, CROPPER_FRAME_PADDING_PX, ZOOM_MAX_RATIO } from "./const";
import { isScreen } from "./utils";
import l10n from "@src/v3.0.0/lib/l10n";
import { dispatchEvent } from "./cropperjs/src/js/utilities";

function _getRepeats() {
  return this.preset.repeats;
}

const getContainerPaddedSize = (value, size) => {
  switch (size) {
    default:
    case "w":
      if (isScreen("md")) {
        return value * CROPPER_FRAME_PADDING_PCT;
      }
      return value - CROPPER_FRAME_PADDING_PX;
    case "h":
      return value - CROPPER_FRAME_PADDING_PX*2;
  }
}

function _getWallAspect() {
  return this.preset.width/this.preset.height;
}

function _getCenter({ width, height }, rounded = false) {
  let left = (this.cropper.offsetWidth - width) / 2,
    top = (this.cropper.offsetHeight - height) / 2;
  if (rounded) {
    left = round(left, 2);
    top = round(top, 2);
  }
  top -= CROPPER_FRAME_OFFSET_TOP_PX;
  return { left, top };
};

function _getContainerAR() {
  const { clientWidth, clientHeight } = this.cropper;
  return Math.floor(getContainerPaddedSize(clientWidth, "w")) / Math.floor(getContainerPaddedSize(clientHeight, "h"));
}

function _getContainerClientSize() {
  const { clientWidth, clientHeight } = this.cropper;
  return {
    clientWidth: getContainerPaddedSize(clientWidth, "w"),
    clientHeight: getContainerPaddedSize(clientHeight, "h"),
  };
}

function _getCropBoxSize(rounded = false) {
  const wallAR = this._getWallAspect();
  const containerAR = this._getContainerAR();
  const { clientWidth, clientHeight } = this._getContainerClientSize();
  let width = clientWidth;
  let height = clientHeight;

  // Se il muro è in landscape o quadrato
  if (wallAR >= 1) {
    // Adatta l'altezza in base alla larghezza del contenitore e al rapporto d'aspetto
    height = clientWidth / wallAR;

    // Se l'altezza così calcolata supera l'altezza del contenitore
    if (height > clientHeight) {
      height = clientHeight;
      width = clientHeight * wallAR;
    } else if (containerAR >= 1 && containerAR > wallAR) {
      // Fluido: adatta anche la larghezza in base all'altezza del contenitore
      height = clientHeight;
      width = clientHeight * wallAR;
    }
  }
  // Se il muro è in portrait
  else {
    // Adatta la larghezza in base all'altezza del contenitore e al rapporto d'aspetto
    width = clientHeight * wallAR;

    // Se la larghezza così calcolata supera la larghezza del contenitore
    if (width > clientWidth) {
      width = clientWidth;
      height = clientWidth / wallAR;
    } else if (containerAR < 1 && containerAR < wallAR) {
      // Fluido: adatta anche l'altezza in base alla larghezza del contenitore
      width = clientWidth;
      height = clientWidth / wallAR;
    }
  }

  // if (rounded) {
  //   width = round(width, 2);
  //   height = round(height, 2);
  // }

  return { width, height };
}

function _getZoomFactor() {
  let zFactor = 1;
  const crop = this._getCropRatio();
  const canvas = this._getCanvasRatio();
  // console.log(crop, canvas);

  if (crop.x >= crop.y) {
    zFactor = crop.x * canvas.x;
  } else if (crop.x < crop.y) {
    if (crop.x < canvas.x) {
      zFactor = crop.x * canvas.x;
    } else if (crop.y > canvas.y) {
      zFactor = crop.y * canvas.y;
    }
  }

  return zFactor;
};

function _updateCropBox() {
  const cropboxZero = { width: 0, height: 0 };
  const cropboxSize = this._getCropBoxSize();
  this.setCropBoxData(assign(cropboxZero, this._getCenter(cropboxZero)));
  this.setCanvasData({ width: 9999 * this._getRepeats() });
  this.setCanvasData(this._getCenter(this.getCanvasData()));
  this.setCropBoxData(assign(cropboxSize, this._getCenter(cropboxSize)));
}

function _updateCanvas($_translate = _translate) {
  const {
    cropBoxData
  } = this;
  const width = cropBoxData.width * $_translate.scale;
  const height = cropBoxData.height * $_translate.scale;
  const left = cropBoxData.left - cropBoxData.width * $_translate.left;
  const top = cropBoxData.top - cropBoxData.height * $_translate.top;  

  this.setCanvasData({ width, height });
  this.setCanvasData({ left, top });
}

const _translateTimeout = 150;
const _translate = {
  top: 0,
  left: 0,
  scale: 1,
};

const calculateOffset = (containerSize, canvasPosition, cropBoxSize) => (
  (containerSize - canvasPosition * 2 - cropBoxSize) / (2 * cropBoxSize)
);

let _translateID;

async function _updateTranslate(debounce = true, onUpdate) {
  const { cropBoxData, containerData, canvasData } = this;
  return new Promise((resolve) => {
    
    function debounce_tranlsate() {
      let scale = canvasData.width / cropBoxData.width;
      
      // scale = Number(scale.toFixed(4));

      Object.assign(_translate, {
        left: calculateOffset(containerData.width, canvasData.left, cropBoxData.width),
        top: calculateOffset(containerData.height, canvasData.top + CROPPER_FRAME_OFFSET_TOP_PX, cropBoxData.height),
        scale: scale,
      });
      onUpdate && onUpdate(_translate);

      resolve(_translate);
    }

    clearTimeout(_translateID);
    const callbackID = () => debounce_tranlsate(onUpdate); 
    if (!debounce) {
      callbackID();
    } else {
      _translateID = setTimeout(callbackID, _translateTimeout);
    }
  });
}

function _calculateInverseOffset(n, axis) {
  const { cropBoxData, containerData, imageData } = this;
  const ratio = imageData.width / imageData.naturalWidth;

  let val;
  if (axis === 'x') {
    val = cropBoxData.left - (n * ratio);
  } else if (axis === 'y') {
    val = cropBoxData.top - (n * ratio) + CROPPER_FRAME_OFFSET_TOP_PX;
  } else {
    throw new Error("Invalid axis, please choose 'x' or 'y'");
  }

  let offset;
  if (axis === 'x') {
    offset = calculateOffset(containerData.width, val, cropBoxData.width);
  } else if (axis === 'y') {
    offset = calculateOffset(containerData.height, val, cropBoxData.height);
  }

  return offset;
};

function _updateAspectRatio() {
  this.setAspectRatio(this.preset.width/this.preset.height);
};

function _updateContainer() {
  this.cropper.style.removeProperty("width");
  this.cropper.style.removeProperty("height");

  const { offsetWidth, offsetHeight } = this.cropper.parentElement;

  this.containerData.height = offsetHeight;
  this.containerData.width = offsetWidth;
}

function _getCropRatio() {
  return {
    x: this.preset.width / l10n.p_dimension_width,
    y: this.preset.height / l10n.p_dimension_height,
  }
};

function _getIsRotated() {
  return Math.abs(this.imageData.rotate) % 180 === 90;
};

function _getCanvasData() {
  const { width, height, ...rest } = this.getCanvasData();
  return {
    width: width / this._getRepeats(),
    height: height / this._getRepeats(),
    ...rest,
  };
};

function _getCanvasRatio() {
  const cropBoxData = this.getCropBoxData();
  const canvasData = this._getCanvasData();
  const rotated = this._getIsRotated();
  const repeats = this._getRepeats();
  const x =
    canvasData[rotated ? "height" : "width"] / cropBoxData.width;
  const y =
    canvasData[rotated ? "width" : "height"] / cropBoxData.height;
  return {
    x: x === Infinity ? 0 : x,
    y: y === Infinity ? 0 : y,
  };
};

function _getMinZoomRatio() {
  const { height, width } = this.cropBoxData;
  const { naturalHeight, naturalWidth } = this.canvasData;
  const crop = this._getCropRatio();
  const canv = this._getCanvasRatio();
  const repeats = this._getRepeats();
  const isPattern = this.preset.isPattern;
  let delta = 1.000000001;
  let minRatio = 1;

  if (this._getIsRotated()) {
    // console.log('%c getIsRotated()', 'background: red');
    if (canv.x < 1) {
      minRatio = height / (naturalHeight / repeats);
    } else {
      minRatio = width / (naturalWidth / repeats);
    }
    if (crop.x <= crop.y) {
      // console.log('crop.x <= crop.y');
      // minRatio /= crop.x/crop.y;
      if (repeats > 1 && !isPattern) {
        minRatio /= repeats;
      }
    } else {
      // console.log('crop.x > crop.y');
    }
  } else {
    
    /**
     * [ [] ]
     * 
     * [[]]
     */
    
    minRatio = width / (naturalWidth / repeats);

    if (crop.x <= crop.y) {
      // console.log('crop.x <= crop.y');
      if (repeats > 1 && !isPattern) {
        // console.log('minRatio /= crop.x / crop.y', crop.x / crop.y);
        minRatio /= crop.x / crop.y;
      } else {
        minRatio *= crop.y / crop.x;
      }
    } else if (crop.x > crop.y) {
      // console.log('crop.x > crop.y');
      if (repeats > 1 && !isPattern) {
        if (crop.x > repeats) {
          // console.log('%');
          minRatio /= (repeats % crop.x) / crop.y;
        } else {
          // console.log('/');
          minRatio /= crop.x / crop.y;
        }
      }
    }
    if (isPattern) {
      // console.log('div');
      minRatio /= repeats;
    }
  }

  // console.log('cr', crop);
  // console.log('rz~', minRatio);

  // return round(minRatio, 2);
  return minRatio * delta;
};

function _initTrasnslate(zero) {
  const { left, top, scale } = this.preset;
  const _prevTranslate = zero ? { left: 0, top: 0, scale: 1 } : { left, top, scale };
  Object.assign(_translate, _prevTranslate);
};

function _getContainerCenterPivot() {
  const rect = this.cropBox.getBoundingClientRect();
  let left = this.container.offsetWidth/2;
  let top = rect.top + (rect.height/2);
  return ({
    x: left,
    y: top,
    pageX: left,
    pageY: top,
    left: left,
    top: top,
  });
};

function _getCanvasCenterPivot() {
  const rect = this.canvas.getBoundingClientRect();
  let x = rect.left + rect.width/2;
  let y = rect.top + rect.height/2;
  return ({
    x,
    y,
    pageX: x,
    pageY: y,
    left: x,
    top: y,
  });
};

function _moveCanvasByOffset(_nextTranslate = {}) {
  Object.assign(_translate, _nextTranslate);
  this._updateFrame();
}

function _zoomRaw(rawValue, pivot = this._getContainerCenterPivot()) {
  const {cropBox, image} = this;
  const crop = this._getCropRatio();
  const repeats = this._getRepeats();
  const zFactor = this._getZoomFactor();
  const minRatio = this._getMinZoomRatio();
  const zDelta = (cropBox.clientWidth / crop.x) / (image.naturalWidth / repeats);
  const value = rawValue <= zFactor ? Math.min(rawValue, zFactor) : rawValue;
  const ratio = value * zDelta;
  this.zoomTo(Math.max(minRatio, ratio), pivot);
  return rawValue;
};

function _updateFrame(firstTime) {
  this._updateContainer();
  this._updateAspectRatio();
  this._updateCropBox();
  this._updateCanvas();

  if (firstTime && !this.preset.edited) {
    this.setCanvasData(this._getCenter(this.getCanvasData()));
  }

  if (this.cropped) {
    this.renderCropBox();
    this.renderCanvas();
  }

  this._updateTranslate(firstTime);
}

function _initFrame(zeroTranslate = false) {
  this._initTrasnslate(zeroTranslate);
  this._updateFrame(zeroTranslate);
}

function _getCustomPrintData() {
  const cropperData = this.getData();
  const { timeout, ...restTranslate } = this._translate;
  return {
    ...restTranslate,
    ...this.preset,
    /** values @2025 */
    px_x: cropperData.x,
    px_y: cropperData.y,
    px_width: cropperData.width,
    px_height: cropperData.height,
    px_natural_width: this.imageData.naturalWidth,
    px_natural_height: this.imageData.naturalHeight,
    /** Legacy values */
    pxX: cropperData.x,
    pxY: cropperData.y,
    pxWidth: cropperData.width,
    pxHeight: cropperData.height,
    pxNaturalWidth: this.imageData.naturalWidth,
    pxNaturalHeight: this.imageData.naturalHeight,
    exceeded_max_zoom: this.zoomExceedsRecommendedValues,
  };
}

const CustomPrint = {
  resize() {
    this._updateFrame(false);
  },
  _translate,
  get zoomExceedsRecommendedValues() {
    return this?.preset?.zoom >= ZOOM_MAX_RATIO
  },
  __() {
    return [
      l10n.p_dimension_width/l10n.p_dimension_height,
      this.canvasData.aspectRatio
    ]
    // return {
    //   pAscpect: l10n.p_dimension_width/l10n.p_dimension_height,
    //   iAspect: this.canvasData.aspectRatio,
    //   realMinZoom: this.canvasData.width / this.canvasData.naturalWidth,
    //   scale: this._translate.scale
    // };
  },
  _getCustomPrintData,
  _moveCanvasByOffset,
  _zoomRaw,
  _calculateInverseOffset,
  _initFrame,
  _updateFrame,
  _updateContainer,
  _updateTranslate,
  _updateAspectRatio,
  _updateCropBox,
  _updateCanvas,
  _initTrasnslate,
  _getCanvasData,
  _getRepeats,
  _getZoomFactor,
  _getIsRotated,
  _getCanvasRatio,
  _getCropRatio,
  _getCenter,
  _getWallAspect,
  _getMinZoomRatio,
  _getContainerClientSize,
  _getContainerCenterPivot,
  _getCanvasCenterPivot,
  _getCropBoxSize,
  _getContainerAR,
};

export default CustomPrint;